<script setup lang="ts">
import { searchResources } from '@/services/rc.service'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { ref, computed } from 'vue'
import { Icon } from '@iconify/vue'
import { NotifyFail } from '@/helpers/core/notifications'

const emit = defineEmits(['change'])

const props = withDefaults(defineProps<{ disabled?: boolean; placeholder?: string }>(), {
  disabled: false,
  placeholder: 'Work Order Number',
})

const workOrderSelected = defineModel<{ number?: string; customer?: any } | undefined>({ required: false })

// Work Order Number search state
const workOrderSearchLoading = ref<boolean>(false)
const workOrderSearchResults = ref<Array<OptionType[]>>([])
const workOrderSearchQuery = ref<string>('')
const workOrderSearchEmptyMessage = computed<string>(() => {
  return workOrderSearchQuery.value.length < 3 ? 'Please enter at least three (3) characters.' : 'No data.'
})

/**
 * Work Order `ElSelectV2` remote search method.
 *
 * @param query work order number (search query).
 */
const searchWorkOrders = async (query: string) => {
  workOrderSearchQuery.value = query
  if (query !== '' && query.length > 2) {
    try {
      workOrderSearchLoading.value = true
      const results = await searchResources({ resource: 'work_order_number', fragment: query })
      workOrderSearchResults.value = results.map((result: any) => {
        return {
          value: { number: result.work_order_number, customer: result.customer },
          label: result.work_order_number,
        }
      })
    } catch {
      NotifyFail('Search failed...')
    } finally {
      workOrderSearchLoading.value = false
    }
  } else {
    workOrderSearchResults.value = []
  }
}

const onChange = (val?: any) => {
  emit('change', val)
}
</script>

<template>
  <el-select-v2
    v-model="workOrderSelected"
    :options="workOrderSearchResults"
    value-key="number"
    remote
    :remote-method="searchWorkOrders"
    :loading="workOrderSearchLoading"
    :no-data-text="workOrderSearchEmptyMessage"
    :placeholder="props.placeholder"
    clearable
    filterable
    :disabled="disabled"
    data-testid="work-order-select"
    @change="onChange"
  >
    <template #loading>
      <div class="remote-search--loading">
        <Icon icon="eos-icons:three-dots-loading" height="20" /></div
    ></template>
  </el-select-v2>
</template>

<style scoped lang="scss">
.remote-search--loading {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
